import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { ATTENDANCE_STATUSES, formatDate, monday as mondayOf, toLocalISODate, parseLocalDate, addDaysToISODate, mondayOfISODate } from './constants';
import AcademicAttendanceTrendline from './AcademicAttendanceTrendline';
import { computeAcademicWeeklyStats } from '../../lib/academicAttendance';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 72.C / 75.1 — day-level statuses. "Late - Justified" is split from
// "Late" so summaries can show punctuality context without lowering the rate.
const DAY_STATUSES = ['Present', 'Late', 'Late - Justified', 'Absent', 'Excused', 'Online', 'No Show', 'No Class'];
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Phase 75.2 — build calendar-aligned weeks. We work with YYYY-MM-DD strings
// throughout (never UTC), so the displayed week stays exactly on the days the
// teacher picked (e.g. Mon 30 Mar 2026 → Sun 05 Apr 2026), even across DST.
const blank = (weekStartISO) => {
  const ws = weekStartISO || mondayOfISODate();
  const we = addDaysToISODate(ws, 6);
  return {
    week_start_date: ws,
    week_end_date: we,
    status: 'Present',
    note: '',
    days: buildDays(ws),
  };
};

const buildDays = (weekStartISO) => {
  // weekStartISO is already YYYY-MM-DD. We synthesise each day by adding
  // calendar days to that string — no toISOString() / no UTC conversions.
  const out = [];
  for (let i = 0; i < 7; i++) {
    out.push({
      date: addDaysToISODate(weekStartISO, i),
      day_label: DAY_LABELS[i],
      status: 'Present',
      note: '',
    });
  }
  return out;
};

// Phase 5 — delegate to the shared academic attendance helper so this panel
// always matches the Trendline, Dashboard, and Season Report numbers.
const computeWeekSummary = (days) => {
  if (!days || days.length === 0) return null;
  const s = computeAcademicWeeklyStats(days);
  return {
    counts: s.breakdown,
    scheduled: s.scheduled_days,
    attended: s.attended_count,
    rate: Math.round(s.attendance_rate), // Panel summary still shows integer %.
  };
};

const AcademicAttendancePanel = ({ attendance, athlete, profile, canEdit = false, onChanged }) => {
  const [dlg, setDlg] = useState({ open: false, editing: null, data: blank() });

  const setData = (patch) => setDlg(s => ({ ...s, data: { ...s.data, ...patch } }));
  const canCreate = canEdit && athlete?.id && profile?.id;

  // When the week_start_date changes, rebuild the day rows (preserving statuses
  // is intentional — the UI prefills Monday-Sunday so the teacher only edits exceptions).
  // Phase 75.2 — use the safe date-only helpers so the calendar week stays
  // anchored to the day the teacher picked (no UTC shift).
  useEffect(() => {
    if (!dlg.open || dlg.editing) return;
    setDlg(s => {
      const ws = s.data.week_start_date;
      return {
        ...s,
        data: {
          ...s.data,
          week_end_date: addDaysToISODate(ws, 6),
          days: buildDays(ws),
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dlg.data.week_start_date, dlg.open, dlg.editing]);

  const openAdd = () => setDlg({ open: true, editing: null, data: blank() });

  const openEdit = (record) => {
    const days = (record.days && record.days.length > 0)
      ? record.days.map(d => ({ ...d }))
      : buildDays(record.week_start_date);
    setDlg({
      open: true,
      editing: record,
      data: {
        week_start_date: record.week_start_date,
        week_end_date: record.week_end_date,
        status: record.status || 'Present',
        note: record.note || '',
        days,
      },
    });
  };

  const updateDay = (idx, patch) => {
    const next = [...dlg.data.days];
    next[idx] = { ...next[idx], ...patch };
    setData({ days: next });
  };

  const markAll = (status) => {
    setData({ days: dlg.data.days.map(d => ({ ...d, status })) });
  };

  const clearWeek = () => {
    setData({ days: dlg.data.days.map(d => ({ ...d, status: 'Present', note: '' })), note: '' });
  };

  const summary = useMemo(() => computeWeekSummary(dlg.data.days), [dlg.data.days]);

  const submit = async () => {
    // Derive the legacy weekly summary status from the breakdown so list views
    // that only know about the old `status` field keep working.
    let summaryStatus = dlg.data.status;
    if (summary && summary.scheduled > 0) {
      if (summary.rate === 100) summaryStatus = 'Present';
      else if (summary.counts['No Show'] > 0) summaryStatus = 'No Show';
      else if (summary.counts.Absent > 0) summaryStatus = 'Absent';
      else if (summary.counts.Excused > 0 && summary.counts.Present === 0) summaryStatus = 'Excused';
      else if (summary.counts.Late > 0 && summary.counts.Absent === 0) summaryStatus = 'Late';
      else if (summary.counts.Online > 0 && summary.counts.Present === 0) summaryStatus = 'Online';
      else summaryStatus = 'Present';
    }
    try {
      const payload = {
        ...dlg.data,
        status: summaryStatus,
        academic_profile_id: profile.id,
        athlete_id: athlete.id,
      };
      if (dlg.editing) {
        await axios.put(`${API}/academic/attendance/${dlg.editing.id}`, payload);
        toast.success('Weekly attendance updated');
      } else {
        await axios.post(`${API}/academic/attendance`, payload);
        toast.success('Weekly attendance recorded');
      }
      setDlg({ open: false, editing: null, data: blank() });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save weekly attendance');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this weekly attendance record?')) return;
    try {
      await axios.delete(`${API}/academic/attendance/${id}`);
      toast.success('Weekly attendance deleted');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-2" data-testid="academic-attendance-list">
      {/* Phase 72.D — Trendline (last 8 saved weeks). */}
      <AcademicAttendanceTrendline attendance={attendance} />

      {canCreate && (
        <div className="flex justify-end">
          <Button size="sm" onClick={openAdd} data-testid="add-attendance-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Week
          </Button>
        </div>
      )}

      {attendance.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No weekly attendance recorded yet.</p>
      ) : attendance.map(a => {
        const wkSum = computeWeekSummary(a.days);
        return (
          <div key={a.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800" data-testid={`academic-attendance-${a.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Week of {formatDate(a.week_start_date)} – {formatDate(a.week_end_date)}
                </p>
                {wkSum ? (
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[11px]">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" data-testid={`week-rate-${a.id}`}>Rate: {wkSum.rate}%</span>
                    {Object.entries(wkSum.counts).filter(([_, n]) => n > 0).map(([k, n]) => (
                      <span key={k} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">{k}: {n}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">{a.status}{a.note ? ` • ${a.note}` : ''}</p>
                )}
              </div>
              {canCreate && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-attendance-${a.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(a.id)} data-testid={`delete-attendance-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dlg.editing ? 'Edit Week' : 'Add Weekly Attendance'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Week Start (Monday)</Label>
                <Input type="date" value={dlg.data.week_start_date}
                       onChange={(e) => setData({ week_start_date: e.target.value })}
                       data-testid="week-start-input" />
              </div>
              <div>
                <Label>Week End (Sunday)</Label>
                <Input type="date" value={dlg.data.week_end_date}
                       onChange={(e) => setData({ week_end_date: e.target.value })}
                       data-testid="week-end-input" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => markAll('Present')} data-testid="mark-all-present-btn">Mark all Present</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => markAll('No Class')} data-testid="mark-all-noclass-btn">Mark all No Class</Button>
              <Button type="button" size="sm" variant="ghost" onClick={clearWeek} data-testid="clear-week-btn">Reset</Button>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
              {dlg.data.days.map((d, idx) => (
                <div key={idx} className="grid grid-cols-[110px_120px_140px_1fr] gap-2 items-center" data-testid={`day-row-${idx}`}>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{d.day_label}</span>
                  <span className="text-xs text-gray-500">{d.date}</span>
                  <Select value={d.status} onValueChange={(v) => updateDay(idx, { status: v })}>
                    <SelectTrigger data-testid={`day-status-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={d.note || ''} onChange={(e) => updateDay(idx, { note: e.target.value })}
                         placeholder="Note (optional)" data-testid={`day-note-${idx}`} />
                </div>
              ))}
            </div>

            {summary && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex flex-wrap gap-2 text-xs" data-testid="week-summary">
                <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                  Weekly Rate: <strong>{summary.rate}%</strong> ({summary.attended}/{summary.scheduled} school days)
                </span>
                {Object.entries(summary.counts).filter(([_, n]) => n > 0).map(([k, n]) => (
                  <span key={k} className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">{k}: {n}</span>
                ))}
              </div>
            )}

            <div>
              <Label>Weekly Note (optional)</Label>
              <Textarea rows={2} value={dlg.data.note}
                        onChange={(e) => setData({ note: e.target.value })}
                        placeholder="Any extra context for the whole week..."
                        data-testid="week-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(s => ({ ...s, open: false }))}>Cancel</Button>
            <Button onClick={submit} data-testid="save-attendance-btn">{dlg.editing ? 'Save' : 'Create week'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademicAttendancePanel;

// Backwards-compat: re-export ATTENDANCE_STATUSES from constants for any
// stale imports across the codebase.
export { ATTENDANCE_STATUSES };
